import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { createUseStyles, Input, Textarea, Button, Advice, SectionForm, Checkbox, Text, Spacer, useTheme } from '@Sage/hoi-poi-ui';
import { getTools } from "../../../../../lib/services/copilotAgents";
import CopilotSidebarTabHeader from "../../components/CopilotSidebarTabHeader";
import { CopilotContext } from "../../../Context";
import styles from "./styles";
const useStyles = createUseStyles(styles, 'CopilotSidebarAgentsForm');
var _ref3 = /*#__PURE__*/React.createElement(Advice, {
  type: "semanticInfo",
  title: "\u2728 Let's bring your agent to life!",
  showIcon: true,
  defaultCollapsed: false
}, "\u2705 Be clear about the agent's role (e.g. Help users understand product features). ", /*#__PURE__*/React.createElement("br", null), "\uD83D\uDDE3\uFE0F Define the tone (e.g. friendly, professional, concise). ", /*#__PURE__*/React.createElement("br", null), "\uD83E\uDDE0 Mention any specific knowledge or context it should use. ", /*#__PURE__*/React.createElement("br", null), "\uD83D\uDEAB Add limits (e.g. Don't give legal advice).");
var _ref4 = /*#__PURE__*/React.createElement(Spacer, {
  y: 3
});
const CopilotSidebarAgentsForm = _ref => {
  let {
    agent,
    onCancel,
    onSave
  } = _ref;
  const theme = useTheme();
  const classes = useStyles();
  const {
    refreshAgents
  } = useContext(CopilotContext);
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState(agent || {
    name: '',
    description: '',
    tools: []
  });
  const [tools, setTools] = useState();
  const selectedTools = useMemo(() => {
    var _form$tools;
    return (form === null || form === void 0 ? void 0 : (_form$tools = form.tools) === null || _form$tools === void 0 ? void 0 : _form$tools.map(tool => tool.name)) || [];
  }, [form === null || form === void 0 ? void 0 : form.tools]);
  const onChange = useCallback(key => value => {
    setForm(_objectSpread(_objectSpread({}, form), {}, {
      [key]: value
    }));
  }, [form]);
  const onInnerCancel = useCallback(() => {
    onCancel();
    setTimeout(() => {
      setForm({
        name: '',
        description: ''
      });
    });
  }, [onCancel]);
  const onInnerSave = useCallback(() => {
    setIsLoading(true);
    onSave(form).then(() => {
      onInnerCancel();
    }).finally(() => {
      setIsLoading(false);
      refreshAgents();
    });
  }, [form, onInnerCancel, onSave, refreshAgents]);
  const onChangeTool = useCallback(name => {
    if (selectedTools.includes(name)) {
      setForm(_objectSpread(_objectSpread({}, form), {}, {
        tools: form.tools.filter(tool => tool.name !== name)
      }));
    } else {
      setForm(_objectSpread(_objectSpread({}, form), {}, {
        tools: [...form.tools, tools.find(tool => tool.name === name)]
      }));
    }
  }, [form, selectedTools, tools]);
  useEffect(() => {
    getTools().then(_ref2 => {
      let {
        data
      } = _ref2;
      setTools(data);
    }).catch(error => {
      console.error(error);
    });
  }, []);
  const toolsOptions = useMemo(() => {
    return tools === null || tools === void 0 ? void 0 : tools.map(tool => /*#__PURE__*/React.createElement("div", {
      className: classes.tool,
      key: tool.name
    }, /*#__PURE__*/React.createElement(Checkbox, {
      className: classes.toolCheckbox,
      checked: selectedTools.includes(tool.name),
      onChange: () => onChangeTool(tool.name),
      color: "actionMinor"
    }), /*#__PURE__*/React.createElement("div", {
      className: classes.toolContent
    }, /*#__PURE__*/React.createElement(Text, {
      bold: true
    }, tool.label || tool.name), /*#__PURE__*/React.createElement(Text, {
      type: "caption",
      color: theme.colors.textLight.secondary
    }, tool.description))));
  }, [classes.tool, classes.toolCheckbox, classes.toolContent, onChangeTool, selectedTools, theme.colors.textLight.secondary, tools]);
  return /*#__PURE__*/React.createElement("div", {
    className: classes.root
  }, /*#__PURE__*/React.createElement(CopilotSidebarTabHeader, {
    title: agent !== null && agent !== void 0 && agent.id ? 'Edit agent' : 'Create agent',
    onBack: onInnerCancel,
    sideComponent: /*#__PURE__*/React.createElement(Button, {
      type: "primary",
      onClick: onInnerSave,
      isLoading: isLoading,
      isDisabled: !form.name || !form.description || !form.instructions
    }, "Save")
  }), _ref3, /*#__PURE__*/React.createElement(SectionForm, {
    title: "Information"
  }, /*#__PURE__*/React.createElement(Input, {
    label: "Name",
    placeholder: "Write the name of the agent",
    value: form.name,
    onChange: onChange('name'),
    isFullWidth: true,
    isRequired: true
  }), /*#__PURE__*/React.createElement(Textarea, {
    label: "Description",
    placeholder: "Write a description of the agent",
    value: form.description,
    onChange: onChange('description'),
    minRows: 2,
    maxRows: 4,
    isResizable: false,
    isFullWidth: true,
    isRequired: true
  }), /*#__PURE__*/React.createElement(Textarea, {
    className: classes.instructions,
    label: "Instructions",
    placeholder: "Need inspiration? Try: \u201CYou are a helpful assistant that guides users through the sales process in a friendly and proactive way. Always suggest the next best action and keep answers short and clear.\u201D",
    value: form.instructions,
    onChange: onChange('instructions'),
    minRows: 10,
    maxRows: 40,
    isFullWidth: true,
    isRequired: true
  })), (tools === null || tools === void 0 ? void 0 : tools.length) > 0 && /*#__PURE__*/React.createElement(SectionForm, {
    title: "Tools"
  }, _ref4, /*#__PURE__*/React.createElement("div", {
    className: classes.tools
  }, toolsOptions)));
};
export default CopilotSidebarAgentsForm;